import React, { useState, useEffect } from 'react';
import { Card, Button, Input, message, Spin, Tag, Alert, Statistic, Row, Col, Pagination } from 'antd';
import { CopyOutlined, GiftOutlined, TeamOutlined, DollarOutlined, LinkOutlined, TrophyOutlined } from '@ant-design/icons';
import { userAPI } from '../services/api';
import { BASE_URL } from '../utils/apiUrl';
import { copyToClipboard } from '../utils/clipboard';
import { t } from '../utils/i18n';

const Affiliate = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [affiliateInfo, setAffiliateInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [totalRebate, setTotalRebate] = useState(0);
  const [inviteCount, setInviteCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const userRes = await userAPI.getInfo();
      if (userRes.code === 0) {
        setUserInfo(userRes.data);
      }

      try {
        const affiliateRes = await fetch(`${BASE_URL}/api/affiliate`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('user_token')}`,
          },
        });
        const affiliateData = await affiliateRes.json();
        if (affiliateData.code === 0) {
          setAffiliateInfo(affiliateData.data);
          setTotalRebate((affiliateData.data.total_rebate || 0) / 100000); // 转为美元
          setInviteCount(affiliateData.data.invite_count || 0);
        }
      } catch (e) {
        console.log('暂无代理信息');
      }

      try {
        const campaignRes = await fetch(`${BASE_URL}/api/referral/campaign`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('user_token')}`,
          },
        });
        const campaignData = await campaignRes.json();
        if (campaignData.code === 0 && campaignData.data) {
          setCampaigns([campaignData.data]);
        }
      } catch (e) {
        console.log('暂无活动');
      }
    } catch (error) {
      console.error('获取数据失败', error);
    } finally {
      setLoading(false);
    }
  };

  const getReferralLink = () => {
    const baseUrl = window.location.origin;
    const code = userInfo?.affiliate_code || '';
    if (code) {
      return `${baseUrl}/r/${code}`;
    }
    const affiliateCode = affiliateInfo?.code || '';
    return affiliateCode ? `${baseUrl}/r/${affiliateCode}` : '';
  };

  const copyLink = async (link) => {
    const success = await copyToClipboard(link);
    if (success) {
      message.success('邀请链接已复制到剪贴板');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const referralLink = getReferralLink();
  // 所有用户都有邀请码，邀请链接总是显示
  const hasReferralCode = referralLink && referralLink.length > 0;

  return (
    <div style={{ padding: '12px 16px', maxWidth: '100%' }}>
      {/* 统计卡片 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} className="stat-card">
            <Statistic
              title="累计返利"
              value={totalRebate}
              prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
              suffix="USD"
              valueStyle={{ color: '#52c41a', fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} className="stat-card">
            <Statistic
              title="已邀请人数"
              value={inviteCount}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              suffix="人"
              valueStyle={{ color: '#1890ff', fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} className="stat-card">
            <Statistic
              title="当前返利比例"
              value={campaigns[0]?.bonus_amount || 0}
              prefix={<><TrophyOutlined style={{ color: '#e03997' }} /> $</>}
              suffix="/人"
              valueStyle={{ color: '#e03997', fontWeight: 600 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 邀请链接卡片 */}
      <Card 
        bordered={false} 
        className="affiliate-card"
        style={{ marginBottom: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
          <div style={{ 
            width: 48, 
            height: 48, 
            borderRadius: 12, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <GiftOutlined style={{ fontSize: 24, color: '#fff' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>邀请返利活动</h3>
            <p style={{ margin: 0, color: '#666', fontSize: 14 }}>分享邀请链接，好友充值即得返利</p>
          </div>
        </div>

        {hasReferralCode ? (
          <div>
            <Alert
              message={t('your_invitation_link')}
              description={t('referral_rewards_desc')}
              type="info"
              showIcon
              icon={<LinkOutlined />}
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Input 
                value={referralLink} 
                readOnly 
                style={{ flex: 1, fontWeight: 500, borderRadius: 8, height: 44 }} 
              />
              <Button 
                type="primary" 
                icon={<CopyOutlined />} 
                onClick={() => copyLink(referralLink)}
                size="large"
                style={{ borderRadius: 8, height: 44 }}
              >
                {t('copy_link')}
              </Button>
            </div>
          </div>
        ) : (
          <Alert
            message="加载中..."
            description="正在获取您的邀请链接"
            type="info"
            showIcon
          />
        )}
      </Card>

      {/* 活动详情 */}
      {campaigns.length > 0 && (
        <Card bordered={false} className="affiliate-card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
            <div style={{ 
              width: 48, 
              height: 48, 
              borderRadius: 12, 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TrophyOutlined style={{ fontSize: 24, color: '#fff' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>当前活动</h3>
              <Tag color="purple" style={{ marginTop: 4 }}>{campaigns[0]?.name}</Tag>
            </div>
          </div>

          <div style={styles.activityInfo}>
            <div style={styles.activityItem}>
              <span style={styles.activityLabel}>返利金额</span>
              <span style={styles.activityValue}>每邀请1位好友充值，返利 ${campaigns[0]?.bonus_amount}</span>
            </div>
            <div style={styles.activityItem}>
              <span style={styles.activityLabel}>活动说明</span>
              <span style={styles.activityValue}>
                {campaigns[0]?.description || '分享您的邀请链接，好友通过链接注册并完成充值即可获得返利'}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* 返利记录 */}
      <Card bordered={false} className="affiliate-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ 
            width: 48, 
            height: 48, 
            borderRadius: 12, 
            background: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DollarOutlined style={{ fontSize: 24, color: '#fff' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>我的返利记录</h3>
            <p style={{ margin: 0, color: '#666', fontSize: 14 }}>查看您的返利明细</p>
          </div>
        </div>
        <ReferralRecords />
      </Card>

      <style>{`
        .stat-card {
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }
        .stat-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
        .affiliate-card {
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
      `}</style>
    </div>
  );
};

const ReferralRecords = () => {
  const [campaignRecords, setCampaignRecords] = useState([]);
  const [merchantRecords, setMerchantRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('user_token');
      const response = await fetch(`${BASE_URL}/api/referral/records`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.code === 0) {
        // 平台活动返利记录
        setCampaignRecords(data.data?.campaign_records || []);
        // 商户拉新佣金记录
        setMerchantRecords(data.data?.merchant_referral_records || []);
      }
    } catch (error) {
      console.error('获取返利记录失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 合并所有记录
  const allRecords = [
    ...campaignRecords.map(r => ({ ...r, recordType: 'campaign' })),
    ...merchantRecords.map(r => ({ ...r, recordType: 'merchant' })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // 分页数据
  const paginatedRecords = allRecords.slice((page - 1) * pageSize, page * pageSize);
  const total = allRecords.length;

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>;
  }

  if (allRecords.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: 40, 
        color: '#999',
        background: '#fafafa',
        borderRadius: 8
      }}>
        <GiftOutlined style={{ fontSize: 48, color: '#ddd', marginBottom: 16 }} />
        <p style={{ fontSize: 14 }}>暂无返利记录，分享邀请链接开始赚取返利吧</p>
      </div>
    );
  }

  const RecordItem = ({ record, isMerchant }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: 8,
        border: '1px solid #f0f0f0',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f0f4f8';
        e.currentTarget.style.borderColor = '#d9d9d9';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#f8fafc';
        e.currentTarget.style.borderColor = '#f0f0f0';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ 
          width: 40, 
          height: 40, 
          borderRadius: '50%',
          background: isMerchant 
            ? 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)'
            : 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {isMerchant ? <GiftOutlined style={{ color: '#fff' }} /> : <TeamOutlined style={{ color: '#fff' }} />}
        </div>
        <div>
          <div style={{ fontWeight: 500, color: '#333' }}>
            {isMerchant ? t('merchant_referral_bonus') : t('friend_referral_bonus')}
            {record.referee_email && (
              <span style={{ color: '#666', fontSize: 12, marginLeft: 8 }}>
                ({record.referee_email})
              </span>
            )}
          </div>
          <div style={{ color: '#999', fontSize: 12 }}>
            {new Date(record.created_at).toLocaleString('zh-CN')}
          </div>
        </div>
      </div>
      <div style={{ 
        color: '#52c41a', 
        fontWeight: 'bold', 
        fontSize: 18,
        padding: '4px 12px',
        background: '#f6ffed',
        borderRadius: 4,
        border: '1px solid #b7eb8f'
      }}>
        +${record.amount?.toFixed(2) || record.bonus_amount?.toFixed(2) || '0.00'}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {paginatedRecords.map((record, index) => (
        <RecordItem key={index} record={record} isMerchant={record.recordType === 'merchant'} />
      ))}
      {total > pageSize && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={setPage}
            showSizeChanger={false}
            showTotal={(total) => `共 ${total} 条记录`}
          />
        </div>
      )}
    </div>
  );
};

const styles = {
  activityInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 16px',
    background: '#f8fafc',
    borderRadius: 8,
  },
  activityLabel: {
    minWidth: 80,
    color: '#666',
    fontSize: 14,
  },
  activityValue: {
    flex: 1,
    color: '#333',
    fontSize: 14,
  },
};

export default Affiliate;
